import { useApolloClient, useMutation } from '@apollo/client';

import type { TeamSkillRow } from './team-detail-model';
import {
  REMOVE_SETTINGS_TEAM_MEMBER,
  UNSHARE_SETTINGS_TEAM_CUSTOM_SKILL,
  UNSHARE_SETTINGS_TEAM_BUILT_IN_SKILL,
  type SettingsTeamDetails,
} from '../../../../queries/skill-administration';
import { DELETE_SKILL_GROUP } from '../../../../queries/skill-management';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import {
  removeTeamFromCache,
  updateCachedSkillGroupAccess,
  updateCachedTeamDetails,
} from '../../lib/skill-group-cache';
import { useSettingsRemoval } from '../settings-shared/use-settings-removal';

export function useTeamRemovalActions(group: SettingsTeamDetails | undefined, onDeleted: () => void) {
  const client = useApolloClient();
  const { settingsAdministration, userUuid } = useChatOverlayContext();
  const permitted =
    !!userUuid &&
    !!group?.parentGroupUuid &&
    settingsAdministration.status === 'ready' &&
    settingsAdministration.teams.some(
      (team) => team.uuid === group.uuid && team.parentGroupUuid === group.parentGroupUuid,
    );
  const action = useSettingsRemoval(group?.uuid ?? '', permitted, userUuid);
  // Apply only confirmed results, after checking that this user and team are still active.
  const options = { errorPolicy: 'none' as const, fetchPolicy: 'no-cache' as const };
  const [removeMember] = useMutation(REMOVE_SETTINGS_TEAM_MEMBER, options);
  const [unshareCustom] = useMutation(UNSHARE_SETTINGS_TEAM_CUSTOM_SKILL, options);
  const [unshareBuiltIn] = useMutation(UNSHARE_SETTINGS_TEAM_BUILT_IN_SKILL, options);
  const [deleteGroup] = useMutation(DELETE_SKILL_GROUP, options);

  const removePerson = (person: { userUuid: string; name: string }) => {
    if (!group?.members.includes(person.userUuid)) return;
    return action.run(
      `member:${person.userUuid}`,
      async () => {
        const { data } = await removeMember({ variables: { groupId: group.uuid, userUuid: person.userUuid } });
        const updated = data?.removeUserFromSkillGroup;
        if (
          updated?.uuid !== group.uuid ||
          updated.parentGroupUuid !== group.parentGroupUuid ||
          updated.members.includes(person.userUuid)
        ) {
          throw new Error('The member removal was not confirmed. Try again.');
        }
        return updated;
      },
      (updated) => updateCachedTeamDetails(client.cache, group.uuid, { members: updated.members }),
      `${person.name} removed from the team.`,
    );
  };

  const revokeSkill = (skill: TeamSkillRow) => {
    if (!group) return;
    return action.run(
      skill.key,
      async () => {
        const { data } =
          skill.kind === 'custom'
            ? await unshareCustom({ variables: { groupId: group.uuid, skillUuid: skill.uuid } })
            : await unshareBuiltIn({ variables: { groupId: group.uuid, commandHandle: skill.commandHandle } });
        const updated = data?.group;
        if (
          updated?.uuid !== group.uuid ||
          (skill.kind === 'custom'
            ? updated.skillUuids.includes(skill.uuid)
            : updated.builtInSkillCommandHandles.includes(skill.commandHandle))
        ) {
          throw new Error('The skill revocation was not confirmed. Try again.');
        }
        return updated;
      },
      (updated) => {
        updateCachedTeamDetails(client.cache, group.uuid, {
          skillUuids: updated.skillUuids,
          builtInSkillCommandHandles: updated.builtInSkillCommandHandles,
        });
        if (skill.kind === 'custom') {
          updateCachedSkillGroupAccess(client.cache, { kind: 'revoke', groupId: group.uuid, skillUuid: skill.uuid });
        }
      },
      `${skill.title} revoked from the team.`,
    );
  };

  const deleteTeam = () => {
    if (!group?.parentGroupUuid) return;
    void action.run(
      'delete',
      async () => {
        const { data } = await deleteGroup({ variables: { groupId: group.uuid } });
        if (data?.deleteSkillGroup !== true) throw new Error('The team was not deleted. Try again.');
      },
      () => {
        removeTeamFromCache(client.cache, group.uuid);
        settingsAdministration.retry();
        onDeleted();
      },
      'Team deleted.',
    );
  };

  return { ...action, permitted, removePerson, revokeSkill, deleteTeam };
}
